import React from "react";
import { BellRing, BriefcaseEdit, Cancel } from "mdi-material-ui";
import { round } from "lodash";
import RoundIcon from "@components/icons/RoundIcon";
import { Typography } from "@material-ui/core";
import { Body, WithTooltip } from "@components";
import { TableSearchColumn } from "@search/TableSearch";
import locale from "@bonliva-crm/locale";
import { store as globalStore } from "@store";
import { ItemType } from "./utils";
import RowActions from "@bookings/BookingSearch/RowActions";
import NationellaListMark from "@common/NationellaListMark";
import { BookingFinanceSearchStore } from "./BookingFinanceSearchStore";
import { BookingStatusType } from "@graphql";

function getStatus(row: ItemType) {
  const status = row.data.bookingStatus.status;

  switch (status) {
    case BookingStatusType.Canceled:
      return { title: locale.Canceled, icon: <Cancel /> };

    case BookingStatusType.Billed:
      return { title: locale.BookingInvoiced, icon: <RoundIcon type="half" /> };

    case BookingStatusType.Paid:
      return { title: locale.BookingPayed, icon: <RoundIcon type="secondHalf" /> };

    case BookingStatusType.Closed:
      return { title: locale.BookingClosed, icon: <RoundIcon type="filled" /> };

    default:
      return { title: locale.BookingOpen, icon: <RoundIcon /> };
  }
}

export const getColumns = (
  store: BookingFinanceSearchStore
): Array<TableSearchColumn<ItemType>> => [
  {
    label: "",
    dataKey: "attention",
    vAlign: "center",
    width: 48,
    style: { justifyContent: "center", color: "#535353" },
    cellRenderer: ({ rowData }) => (
      <div>
        {rowData.data?.IsMarked && (
          <div>
            <BellRing color="inherit" fontSize="small" />
          </div>
        )}

        {rowData.data?.ConsultantServicePreferences && (
          <div>
            <BriefcaseEdit color="inherit" fontSize="small" />
          </div>
        )}
      </div>
    )
  },
  {
    label: locale.Consultant,
    dataKey: "consultant",
    width: 150,
    vAlign: "center",
    style: { paddingLeft: 0 },
    cellRenderer: ({ rowData }) => <Body>{rowData.data.ConsultantRef.Name}</Body>
  },
  {
    label: locale.Customer,
    // width: 200,
    fitted: true,
    dataKey: "customer",
    vAlign: "center",
    style: { minWidth: 200 },
    cellRenderer: ({ rowData }) => (
      <>
        <div>
          <Body display="block">{rowData.data.OrganizationRef.Name}</Body>
          <Body display="block">{rowData.data.HospitalRef.Name}</Body>
          {rowData.data.DepartmentsRefs.length > 0 && (
            <Body display="block">
              {rowData.data.DepartmentsRefs.map((x) => x.Name).join(", ")}
            </Body>
          )}
        </div>
        {rowData.data.isNationalProcurement && <NationellaListMark />}
      </>
    )
  },
  {
    label: locale.AreaOfExpertise,
    // width: 128,
    dataKey: "aoe",
    vAlign: "center",
    style: { minWidth: 120 },
    cellRenderer: ({ rowData }) => (
      <Typography noWrap style={{ maxWidth: 155 }}>
        {rowData.data.AreaOfExpertise}
      </Typography>
    )
  },
  {
    label: "Kst",
    fitted: true,
    // width: 130,
    vAlign: "center",
    dataKey: "costCenter",
    // style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) =>
      rowData.data.CostCenterName && <Body>{rowData.data.CostCenterName}</Body>
  },
  {
    width: 96,
    label: locale.Project,
    dataKey: "project",
    vAlign: "center",
    cellRenderer: ({ rowData }) => {
      if (!rowData.data?.ProjectId) {
        return null;
      }

      return (
        <Body title={`Projektnr: ${rowData.data?.projectNumber ?? "not found"}`}>
          {rowData.data?.projectName ?? "not found"}
        </Body>
      );
    }
  },
  {
    width: 120,
    label: locale.CallNumber,
    dataKey: "callNumber",
    vAlign: "center",
    cellRenderer: ({ rowData }) => <Body>{rowData.data.callNumber ?? ""}</Body>
  },
  {
    width: 72,
    label: "Alt",
    dataKey: "alt",
    vAlign: "center",
    // style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => (
      <Body style={{ borderBottom: "1px dashed darkkhaki", textAlign: "center" }}>
        {rowData.data.ContractTypeAbbr}
      </Body>
    )
  },
  {
    width: 72,
    label: "Fakt Nr",
    vAlign: "center",
    dataKey: "invoiceNo",
    style: { justifyContent: "flex-end" },
    headerStyle: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => (
      <Body
        color="initial"
        style={{ fontSize: rowData.data.InvoiceNumbers.length > 3 ? "75%" : undefined }}
      >
        {rowData.data.InvoiceNumbers.map(checkCustomInvoiceNo).join(", ")}
      </Body>
    )
  },
  {
    width: 72,
    label: locale.CustomerNumberShort,
    vAlign: "center",
    dataKey: "customerNo",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => <Body>{rowData.data.CustomerNumber}</Body>
  },
  {
    width: 32,
    fitted: true,
    label: "St",
    dataKey: "FinanceStatus",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => {
      const { icon, title } = getStatus(rowData);
      return (
        <WithTooltip title={title}>
          <span>{icon}</span>
        </WithTooltip>
      );
    }
  },
  {
    width: 96,
    fitted: true,
    label: locale.TotalGr,
    dataKey: "invoicedTotal",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => (
      <Typography variant="body2">{rowData.data.InvoicedTotalFmt}</Typography>
    )
  },
  {
    width: 96,
    fitted: true,
    label: "TB1",
    // skip: !store().user.can("booking-totals:read:own"),
    dataKey: "profitNet",
    vAlign: "center",
    cellRenderer: ({ rowData }) => (
      <Typography variant="body2">{rowData.data.ProfitNetFmt}</Typography>
    )
  },
  {
    width: 56,
    fitted: true,
    label: "TG",
    dataKey: "profitRatio",
    // skip: !store().user.can("booking-totals:read:own"),
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => {
      return (
        <Typography variant="body2">
          {typeof rowData.data.ProfitRatio === "number"
            ? `${round(rowData.data.ProfitRatio * 100)}%`
            : ""}
        </Typography>
      );
    }
  },
  {
    width: 50,
    fitted: true,
    label: null,
    dataKey: "actions",
    style: { justifyContent: "center" },
    cellRenderer: ({ rowData }) => {
      return (
        <RowActions
          id={rowData.data.BookingRef.NodeID}
          onCloneBooking={store.cloneBookingForMonthlyBrake}
        />
      );
    }
  }
];

function checkCustomInvoiceNo(invoiceNo: string): string {
  const { specialInvoices } = globalStore().cache;
  const specialInvoiceNumbers = Object.keys(specialInvoices);

  return specialInvoiceNumbers.includes(invoiceNo)
    ? locale.bookingInvoiceType[specialInvoices[invoiceNo]]
    : invoiceNo;
}
